import { REGISTERS_API_URL } from '../constants';
import { type AppDatabase } from '../db';
import { type Discount } from '../models';
import { MAX_FETCH_PROGRESS, retrieveFullPaginatedData } from '../pagination';
import { toBooleanValueFields } from '../transform';
import {
    BOOLEAN_VALUE,
    DISCOUNT_RULE_TYPE,
    DISCOUNT_TYPE_DISPLAY_ORDER,
    type MappedDiscountResponse,
} from '../types';

export class DiscountService {
    constructor(private readonly db: AppDatabase) {
        this.db = db;
    }

    async fetchAndBulkUpsert(
        lastSyncTime?: string,
        progressCallback?: (progress: number) => void,
    ) {
        const params = {
            timestamp: lastSyncTime ?? null,
            isActive: lastSyncTime ? null : true,
        };

        await retrieveFullPaginatedData<MappedDiscountResponse>(
            REGISTERS_API_URL.PRODUCT.DISCOUNTS,
            params,
            async (data, progress) => {
                await this.bulkUpsert(data);
                progressCallback?.(progress);
            },
        );

        progressCallback?.(MAX_FETCH_PROGRESS);
    }

    async fetchNonCustomerDiscounts() {
        const discounts = await this.db.discounts
            .where('[hasRules+deleted]')
            .equals([BOOLEAN_VALUE.FALSE, BOOLEAN_VALUE.FALSE])
            .toArray();

        return discounts.sort((discountA, discountB) => {
            const displayOrderDiff =
                DISCOUNT_TYPE_DISPLAY_ORDER[discountA.rewardType] -
                DISCOUNT_TYPE_DISPLAY_ORDER[discountB.rewardType];

            if (displayOrderDiff !== 0) {
                return displayOrderDiff;
            }

            return discountA.name.localeCompare(discountB.name);
        });
    }

    async fetchDiscountByCustomerGroups(customerGroupIds: string[]) {
        const customerDiscounts = await this.db.discounts
            .where('customerGroupIds')
            .anyOf(customerGroupIds)
            .and((discount) => discount.deleted === BOOLEAN_VALUE.FALSE)
            .toArray();

        return customerDiscounts;
    }

    async bulkUpsert(discounts: MappedDiscountResponse[]) {
        const output = discounts.map((discount) => {
            const customerGroupIds =
                discount.rules?.find(
                    (rule) =>
                        rule.ruleType === DISCOUNT_RULE_TYPE.CUSTOMER_GROUP,
                )?.includings ?? [];

            return toBooleanValueFields(
                {
                    ...discount,
                    hasRules: !!discount.rules?.length,
                    customerGroupIds,
                },
                ['deleted', 'hasRules'],
            );
        });

        await this.db.discounts.bulkPut(output as unknown as Discount[]);
    }
}
